import { useState, useEffect } from "react";
import { apiPost, apiGet } from "../lib/api";
import { COLORS } from "../lib/colors";

export default function ArchiveSection({ token, schoolId, onRefresh }) {
  const [label, setLabel] = useState("");
  const [archiving, setArchiving] = useState(false);
  const [result, setResult] = useState(null);
  const [archives, setArchives] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(null);

  // Fetch past archives on mount
  useEffect(() => {
    if (!token) return;
    const schoolParam = schoolId ? `?school_id=${schoolId}` : "";
    apiGet(`/dashboard/archives${schoolParam}`, token)
      .then(data => setArchives(data.archives || []))
      .catch(() => {});
  }, [token, schoolId]);

  const handleArchive = async () => {
    if (!label.trim()) {
      setResult({ type: "error", message: "Please enter a year label (e.g. 2024-2025)" });
      return;
    }
    if (!schoolId) {
      setResult({ type: "error", message: "No school ID found. Contact your admin." });
      return;
    }

    const confirmed = window.confirm(
      `Archive all students for "${label.trim()}"?\n\nThis will:\n• Save all student profiles and metrics\n• Clear the active talent pool\n• Recruiters will remain active\n\nThis cannot be undone.`
    );
    if (!confirmed) return;

    setArchiving(true);
    setResult(null);
    try {
      const data = await apiPost(
        `/dashboard/archive?school_id=${schoolId}&label=${encodeURIComponent(label.trim())}`,
        {},
        token
      );
      setResult({
        type: "success",
        message: `Archived "${data.label}" — ${data.students_archived} students saved. Pool cleared.`,
      });
      setLabel("");
      // Refresh the archives list and the dashboard
      const schoolParam = schoolId ? `?school_id=${schoolId}` : "";
      const archivesData = await apiGet(`/dashboard/archives${schoolParam}`, token);
      setArchives(archivesData.archives || []);
      onRefresh();
    } catch (err) {
      setResult({ type: "error", message: `Archive failed: ${err.message}` });
    } finally {
      setArchiving(false);
    }
  };

  const toggleExpand = async (archiveId) => {
    if (expandedId === archiveId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(archiveId);

    // Fetch detail if not already loaded
    if (!expandedData[archiveId]) {
      setLoadingDetail(archiveId);
      try {
        const data = await apiGet(`/dashboard/archives/${archiveId}`, token);
        setExpandedData(prev => ({ ...prev, [archiveId]: data }));
      } catch {
        setExpandedData(prev => ({ ...prev, [archiveId]: { error: true } }));
      } finally {
        setLoadingDetail(null);
      }
    }
  };

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
        Year-End Archive
      </h3>
      <p style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 16 }}>
        Snapshot all students and metrics, then clear the pool for the new year
      </p>

      {/* Archive action */}
      <div className="archive-action-row" style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
        <div>
          <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, marginBottom: 4 }}>
            Year Label
          </label>
          <input
            type="text"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="e.g. 2024-2025"
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              border: `1px solid ${COLORS.border}`,
              fontSize: 14,
              fontFamily: "inherit",
              backgroundColor: COLORS.surface,
              color: COLORS.text,
              width: 160,
            }}
          />
        </div>
        <button
          onClick={handleArchive}
          disabled={archiving}
          style={{
            padding: "9px 20px",
            borderRadius: 8,
            border: `1px solid ${COLORS.red}`,
            backgroundColor: COLORS.redLight,
            color: COLORS.red,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "inherit",
            cursor: archiving ? "wait" : "pointer",
            opacity: archiving ? 0.7 : 1,
            transition: "opacity 0.15s",
          }}
        >
          {archiving ? "Archiving..." : "Archive Year"}
        </button>
      </div>

      {result && (
        <div style={{
          padding: "10px 14px",
          borderRadius: 8,
          marginTop: 12,
          backgroundColor: result.type === "success" ? COLORS.greenLight : COLORS.redLight,
          color: result.type === "success" ? COLORS.green : COLORS.red,
          fontSize: 13,
        }}>
          {result.message}
        </div>
      )}

      {/* Past Archives */}
      {archives.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>
            Past Archives
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {archives.map(a => {
              const isExpanded = expandedId === a.id;
              const detail = expandedData[a.id];
              const date = (a.created_at || "").slice(0, 10);

              return (
                <div key={a.id} style={{
                  backgroundColor: COLORS.surface,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 10,
                  overflow: "hidden",
                }}>
                  {/* Header */}
                  <div
                    onClick={() => toggleExpand(a.id)}
                    style={{
                      padding: "12px 16px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>
                        {a.label}
                      </span>
                      <span style={{ fontSize: 12, color: COLORS.textMuted }}>{date}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 12, color: COLORS.textSecondary }}>
                        {a.total_students} students
                      </span>
                      <span style={{
                        fontSize: 12, fontWeight: 600,
                        color: COLORS.green,
                        backgroundColor: COLORS.greenLight,
                        padding: "2px 8px", borderRadius: 4,
                      }}>
                        {a.hired_count} hired
                      </span>
                      <span style={{ fontSize: 14, color: COLORS.textMuted }}>
                        {isExpanded ? "−" : "+"}
                      </span>
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isExpanded && (
                    <div style={{
                      padding: "0 16px 16px",
                      borderTop: `1px solid ${COLORS.borderLight}`,
                      paddingTop: 14,
                    }}>
                      {loadingDetail === a.id && (
                        <p style={{ fontSize: 13, color: COLORS.textMuted }}>Loading...</p>
                      )}

                      {detail && !detail.error && (
                        <>
                          {/* Metrics summary */}
                          <div className="archive-metrics-grid" style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, 1fr)",
                            gap: 10,
                            marginBottom: 16,
                          }}>
                            {[
                              { label: "Total Students", value: detail.archive?.total_students },
                              { label: "Hired", value: detail.archive?.hired_count },
                              { label: "CV Requests", value: detail.archive?.total_cv_requests },
                              { label: "Approval Rate", value: `${detail.archive?.cv_approval_rate || 0}%` },
                            ].map(m => (
                              <div key={m.label} style={{
                                backgroundColor: COLORS.surfaceAlt,
                                borderRadius: 8,
                                padding: "10px 12px",
                              }}>
                                <p style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>{m.label}</p>
                                <p style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{m.value}</p>
                              </div>
                            ))}
                          </div>

                          {/* Student list */}
                          <p style={{ fontSize: 13, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 8 }}>
                            Students ({(detail.students || []).length})
                          </p>
                          <div style={{
                            maxHeight: 300,
                            overflow: "auto",
                            border: `1px solid ${COLORS.borderLight}`,
                            borderRadius: 8,
                          }}>
                            <table style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              fontSize: 13,
                              fontFamily: "inherit",
                            }}>
                              <thead>
                                <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                                  {["Name", "Programme", "Status", "Hired At", "Views", "CV Reqs"].map(h => (
                                    <th key={h} style={{
                                      textAlign: "left", padding: "8px 12px",
                                      fontWeight: 600, color: COLORS.textSecondary, fontSize: 12,
                                      position: "sticky", top: 0, backgroundColor: COLORS.surface,
                                    }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(detail.students || []).map((s, i) => (
                                  <tr key={i} style={{
                                    borderBottom: `1px solid ${COLORS.borderLight}`,
                                  }}>
                                    <td style={{ padding: "8px 12px", color: COLORS.text, fontWeight: 500 }}>{s.name}</td>
                                    <td style={{ padding: "8px 12px", color: COLORS.textSecondary }}>{s.programme}</td>
                                    <td style={{ padding: "8px 12px" }}>
                                      <span style={{
                                        fontSize: 12, fontWeight: 600,
                                        color: s.status === "hired" ? COLORS.green : s.status === "active" ? COLORS.accent : COLORS.textMuted,
                                        backgroundColor: s.status === "hired" ? COLORS.greenLight : s.status === "active" ? COLORS.accentLight : COLORS.surfaceAlt,
                                        padding: "2px 8px", borderRadius: 4,
                                      }}>
                                        {s.status}
                                      </span>
                                    </td>
                                    <td style={{ padding: "8px 12px", color: COLORS.textSecondary }}>{s.hired_company || "—"}</td>
                                    <td style={{ padding: "8px 12px", color: COLORS.textSecondary }}>{s.views}</td>
                                    <td style={{ padding: "8px 12px", color: COLORS.textSecondary }}>{s.cv_requests}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}

                      {detail && detail.error && (
                        <p style={{ fontSize: 13, color: COLORS.red }}>Failed to load archive details.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
